import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  FileButton,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import {
  IconAlertCircle,
  IconFile,
  IconSearch,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { SalonDocument } from "../../types/salonDocument";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

/** Salon-level documents (NOT PHI — stored under `external/`, no KMS/signed URLs).
 *  Patient-specific content must use the client-scoped `phi/` paths instead. */

const documentDisplayName = (d: SalonDocument): string =>
  d.displayName?.trim() || d.documentKey || "Untitled document";

const formatDate = (iso: string): string => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const formatSize = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ACCEPT_TYPES =
  "application/pdf,image/png,image/jpeg,image/heic,image/heif," +
  "application/msword," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const SalonDocumentsSettingsTab: React.FC = () => {
  const { listSalonDocuments, uploadSalonDocument, deleteSalonDocument } =
    useApp() as {
      listSalonDocuments: (
        salonId: string,
      ) => Promise<{ documents: SalonDocument[] }>;
      uploadSalonDocument: (
        salonId: string,
        documentKey: string,
        file: File | Blob,
        contentType: string,
        metadata?: Record<string, unknown>,
        clientId?: string,
        displayName?: string,
      ) => Promise<{ document?: SalonDocument } | unknown>;
      deleteSalonDocument: (
        salonId: string,
        documentKey: string,
        clientId?: string,
      ) => Promise<unknown>;
    };
  const { businessInfo } = useSalon() as {
    businessInfo: { id: string } | null;
  };

  const [documents, setDocuments] = useState<SalonDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SalonDocument | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!businessInfo?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listSalonDocuments(businessInfo.id);
      // Only salon-level documents (client-scoped PHI is filtered out).
      setDocuments((res?.documents ?? []).filter((d) => !d.clientId));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not load documents.",
      );
    } finally {
      setLoading(false);
    }
  }, [businessInfo?.id, listSalonDocuments]);

  useEffect(() => {
    void fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async (file: File | null) => {
    if (!file || !businessInfo?.id) return;
    const salonId = businessInfo.id;
    const newKey =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `doc-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    setUploading(true);
    setError(null);
    try {
      await uploadSalonDocument(
        salonId,
        newKey,
        file,
        file.type || "application/octet-stream",
        undefined,
        undefined, // salon-level (no clientId)
        file.name,
      );
      notifySuccess(`${file.name} is now available across the salon.`, {
        title: "Document uploaded",
      });
      await fetchDocuments();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Upload failed. Try again.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !businessInfo?.id) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteSalonDocument(businessInfo.id, deleteTarget.documentKey);
      notifySuccess(`${documentDisplayName(deleteTarget)} has been removed.`, {
        title: "Document deleted",
      });
      setDeleteTarget(null);
      await fetchDocuments();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Delete failed. Try again.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((d) =>
      documentDisplayName(d).toLowerCase().includes(q),
    );
  }, [documents, search]);

  return (
    <Box p="md">
      <Stack gap={2} mb="md">
        <Title order={3}>Documents</Title>
        <Text size="sm" c="dimmed">
          Generic attachments for post-appointment messages.
        </Text>
      </Stack>

      <Group justify="space-between" align="center" mb="sm">
        <TextInput
          placeholder="Search documents"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<IconSearch size={14} />}
          size="xs"
          style={{ flex: 1, maxWidth: 320 }}
        />
        <FileButton onChange={handleUpload} accept={ACCEPT_TYPES}>
          {(props) => (
            <Button
              {...props}
              size="sm"
              color="var(--brand-teal)"
              leftSection={<IconUpload size={14} />}
              loading={uploading}
              disabled={!businessInfo?.id || uploading}
            >
              {uploading ? "Uploading…" : "Upload document"}
            </Button>
          )}
        </FileButton>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={14} />} mb="sm">
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : filtered.length === 0 ? (
        <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
          <Stack gap={4} align="center">
            <Text size="sm" c="dimmed">
              {search.trim()
                ? "No documents match your search."
                : "No salon-level documents uploaded yet."}
            </Text>
            {!search.trim() && (
              <Text size="xs" c="dimmed">
                Upload a PDF, image, or Word doc above. It will be available to
                attach to post-appointment messages on any service.
              </Text>
            )}
          </Stack>
        </Paper>
      ) : (
        <Stack gap="xs">
          {filtered.map((d) => {
            const name = documentDisplayName(d);
            return (
              <Paper key={d.documentUuid} p="sm" withBorder radius="md">
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Group
                    gap="sm"
                    wrap="nowrap"
                    style={{ minWidth: 0, flex: 1 }}
                  >
                    <IconFile size={20} color="var(--mantine-color-blue-6)" />
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap">
                        {d.currentUrl ? (
                          <Anchor
                            href={d.currentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            fw={600}
                            truncate
                          >
                            {name}
                          </Anchor>
                        ) : (
                          <Text size="sm" fw={600} truncate>
                            {name}
                          </Text>
                        )}
                        {d.currentContentType && (
                          <Badge
                            size="xs"
                            variant="light"
                            color="gray"
                            style={{ textTransform: "none" }}
                          >
                            {d.currentContentType}
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {formatSize(d.currentFileSize)}
                        {d.currentFileSize ? " · " : ""}
                        Uploaded {formatDate(d.currentCreatedDt)}
                        {d.uploadedByName || d.lastSavedByStaffName
                          ? ` by ${d.uploadedByName || d.lastSavedByStaffName}`
                          : ""}
                      </Text>
                    </Stack>
                  </Group>
                  <Tooltip label="Delete document" withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => setDeleteTarget(d)}
                      aria-label={`Delete ${name}`}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      <DeleteConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => (deleting ? undefined : setDeleteTarget(null))}
        onConfirm={handleDelete}
        title="Delete Document"
        message={`Delete "${
          deleteTarget ? documentDisplayName(deleteTarget) : ""
        }"? This cannot be undone, and any post-appointment messages that reference this document will skip it on next send.`}
        confirmLabel="Delete"
        isLoading={deleting}
      />
    </Box>
  );
};

export default SalonDocumentsSettingsTab;
